import { useState, useEffect } from 'react';
import { db, storage } from '@/lib/firebase';
import { useTranslation } from 'react-i18next';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  onSnapshot,
  query,
  orderBy
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  X, 
  Save, 
  Image as ImageIcon,
  Loader2,
  ChevronRight,
  ExternalLink,
  Hash,
  Link as LinkIcon,
  Copy,
  Check
} from 'lucide-react';
import { logActivity } from '@/lib/logger';

async function autoTranslate(text: string, from: string, to: string): Promise<string> {
  try {
    const response = await fetch(`https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=${from}|${to}`);
    const data = await response.json();
    if (data.responseData && data.responseData.translatedText) {
      return data.responseData.translatedText;
    }
    return text;
  } catch (error) {
    console.error("Translation API error:", error);
    return text;
  }
}

interface MenuItem {
  id: string;
  sku: string;
  name: string | { es: string, en: string };
  description: string | { es: string, en: string };
  price: string;
  categoryId: string;
  image: string;
  buyUrl: string;
}

interface Category {
  id: string;
  name: string | { es: string, en: string };
  parentId: string | null;
}

export default function MenuManagementPage() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<MenuItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    sku: '',
    nameEn: '',
    nameEs: '',
    descriptionEn: '',
    descriptionEs: '',
    price: '',
    categoryId: '',
    image: '',
    buyUrl: '',
  });

  useEffect(() => {
    const qMenu = query(collection(db, 'menu_items'), orderBy('name'));
    const unsubMenu = onSnapshot(qMenu, (snapshot) => {
      const menuData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as MenuItem[];
      setItems(menuData);
    });

    const qCat = query(collection(db, 'categories'), orderBy('name'));
    const unsubCat = onSnapshot(qCat, (snapshot) => {
      const catData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Category[];
      setCategories(catData);
      setLoading(false);
    });

    return () => {
      unsubMenu();
      unsubCat();
    };
  }, []);

  const generateHash = () => {
    return Math.random().toString(16).substring(2, 10).toUpperCase();
  };

  const handleOpenModal = (item?: MenuItem) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        sku: item.sku || generateHash(),
        nameEn: typeof item.name === 'string' ? item.name : item.name.en || '',
        nameEs: typeof item.name === 'string' ? '' : item.name.es || '',
        descriptionEn: typeof item.description === 'string' ? item.description : item.description.en || '',
        descriptionEs: typeof item.description === 'string' ? '' : item.description.es || '',
        price: item.price,
        categoryId: item.categoryId,
        image: item.image,
        buyUrl: item.buyUrl || '',
      });
    } else {
      setEditingItem(null);
      setFormData({
        sku: generateHash(),
        nameEn: '',
        nameEs: '',
        descriptionEn: '',
        descriptionEs: '',
        price: '',
        categoryId: categories[0]?.id || '',
        image: '',
        buyUrl: '',
      });
    }
    setIsModalOpen(true);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(text);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const storageRef = ref(storage, `menu/${Date.now()}_${file.name}`);
    
    try {
      const snapshot = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(snapshot.ref);
      setFormData(prev => ({ ...prev, image: url }));
    } catch (error) {
      console.error("Upload error:", error);
      alert("Error uploading image");
    } finally {
      setUploading(false);
    }
  };

  const handleTranslate = async (field: 'name' | 'description', from: string, to: string) => {
    const text = field === 'name' 
      ? (from === 'en' ? formData.nameEn : formData.nameEs)
      : (from === 'en' ? formData.descriptionEn : formData.descriptionEs);
      
    if (!text.trim()) return;
    
    const translated = await autoTranslate(text, from, to);
    
    setFormData(prev => ({
      ...prev,
      [field === 'name' ? (to === 'es' ? 'nameEs' : 'nameEn') : (to === 'es' ? 'descriptionEs' : 'descriptionEn')]: translated
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.categoryId) {
      alert("Please select a category first.");
      return;
    }
    setLoading(true);

    const nameData = {
      en: formData.nameEn.trim(),
      es: formData.nameEs.trim()
    };
    const descData = {
      en: formData.descriptionEn.trim(),
      es: formData.descriptionEs.trim()
    };

    const submitData = {
      sku: formData.sku,
      name: nameData,
      description: descData,
      price: formData.price,
      categoryId: formData.categoryId,
      image: formData.image,
      buyUrl: formData.buyUrl,
      updatedAt: new Date().toISOString()
    };

    try {
      if (editingItem) {
        await updateDoc(doc(db, 'menu_items', editingItem.id), submitData);
        await logActivity('edit', 'menu', `${nameData.en} [${formData.sku}]`);
      } else {
        await addDoc(collection(db, 'menu_items'), submitData);
        await logActivity('create', 'menu', `${nameData.en} [${formData.sku}]`);
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error("Submit error:", error);
      alert("Error saving item");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    const item = items.find(i => i.id === id);
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await deleteDoc(doc(db, 'menu_items', id));
        if (item) {
          await logActivity('delete', 'menu', `${item.name} [${item.sku}]`);
        }
      } catch (error) {
        console.error("Delete error:", error);
        alert("Error deleting item");
      }
    }
  };

  const getCategoryName = (catId: string): string => {
    const cat = categories.find(c => c.id === catId);
    if (!cat) return 'Unknown';
    return typeof cat.name === 'string' ? cat.name : (cat.name[i18n.language] || cat.name.en || '');
  };

  if (loading && items.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="animate-spin text-[#F57F25]" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-black text-white uppercase tracking-tight">{t('admin_menu_title')}</h1>
          <p className="text-white/50 text-sm">{t('admin_menu_sub')}</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="bg-[#F57F25] hover:bg-[#e06d15] text-black font-black px-6 py-3 rounded-2xl flex items-center gap-2 transition-all uppercase text-sm shadow-lg shadow-[#F57F25]/20"
        >
          <Plus size={20} />
          {t('admin_menu_add')}
        </button>
      </div>

      <div className="space-y-12">
        {categories.map(category => {
          const catItems = items.filter(item => item.categoryId === category.id);
          if (catItems.length === 0) return null;

          return (
            <div key={category.id} className="space-y-6">
              <div className="flex items-center gap-2">
                <div className="w-2 h-8 bg-[#F57F25] rounded-full"></div>
                <h2 className="text-xl font-black text-white uppercase tracking-widest">
                  {getCategoryName(category.id)}
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {catItems.map(item => {
                  const resolvedName = typeof item.name === 'string' ? item.name : (item.name[i18n.language] || item.name.en || '');
                  const resolvedDesc = typeof item.description === 'string' ? item.description : (item.description[i18n.language] || item.description.en || '');
                  return (
                    <div key={item.id} className="bg-[#111] border border-white/5 rounded-[32px] overflow-hidden group relative">
                      <div className="h-48 relative">
                        <img src={item.image || 'https://via.placeholder.com/400x300?text=No+Image'} alt={resolvedName} className="w-full h-full object-cover" />
                        <div className="absolute top-4 left-4 flex items-center gap-2">
                          <div className="bg-black/80 backdrop-blur-md px-3 py-1 rounded-full border border-white/10 flex items-center gap-1.5">
                            <Hash size={10} className="text-[#F57F25]" />
                            <span className="text-[9px] font-black text-white tracking-widest uppercase">{item.sku}</span>
                          </div>
                        </div>
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                          <button 
                            onClick={() => handleOpenModal(item)}
                            className="p-3 bg-white text-black rounded-full hover:bg-[#F57F25] transition-colors"
                          >
                            <Pencil size={20} />
                          </button>
                          <button 
                            onClick={() => handleDelete(item.id)}
                            className="p-3 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>
                      </div>
                      <div className="p-6">
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="text-white font-bold text-lg uppercase tracking-tight">
                            {resolvedName}
                          </h3>
                          <span className="text-[#F57F25] font-black">{item.price}</span>
                        </div>
                        <p className="text-white/40 text-xs line-clamp-2 mb-4">
                          {resolvedDesc}
                        </p>
                        
                        {item.buyUrl && (
                          <a 
                            href={item.buyUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="w-full bg-white/5 hover:bg-white/10 border border-white/5 py-2 rounded-xl flex items-center justify-center gap-2 text-[10px] font-black uppercase text-white/50 tracking-widest transition-all"
                          >
                            <ExternalLink size={14} /> {t('admin_menu_buy')}
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-[#111] w-full max-w-3xl rounded-[40px] border border-white/10 shadow-2xl overflow-hidden flex flex-col max-h-[95vh]">
            <div className="p-8 border-b border-white/5 flex justify-between items-center bg-[#181818]">
              <div>
                <h2 className="text-2xl font-black text-white uppercase tracking-tight">
                  {editingItem ? t('admin_menu_edit') : t('admin_menu_new')}
                </h2>
                <p className="text-white/30 text-xs mt-1">{t('admin_menu_details')}</p>
              </div>
              <button onClick={() => setIsModalOpen(false)} className="text-white/40 hover:text-white transition-colors">
                <X size={28} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-8 overflow-y-auto space-y-8 custom-scrollbar">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <label className="text-white/50 text-[10px] font-black uppercase tracking-widest ml-1">{t('admin_menu_hash')}</label>
                  <div className="relative">
                    <Hash className="absolute left-4 top-1/2 -translate-y-1/2 text-[#F57F25]" size={18} />
                    <input
                      type="text"
                      value={formData.sku}
                      readOnly
                      className="w-full bg-black/50 border border-white/5 rounded-2xl py-4 pl-12 pr-12 text-[#F57F25] font-black outline-none cursor-not-allowed select-all"
                    />
                    <button 
                      type="button"
                      onClick={() => copyToClipboard(formData.sku)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-white/20 hover:text-white transition-colors"
                    >
                      {copiedId === formData.sku ? <Check size={18} className="text-green-500" /> : <Copy size={18} />}
                    </button>
                  </div>
                  <p className="text-[9px] text-white/20 uppercase font-black tracking-widest ml-1 italic">{t('admin_menu_hash_help')}</p>
                </div>
                <div className="space-y-2">
                  <label className="text-white/50 text-[10px] font-black uppercase tracking-widest ml-1">{t('admin_menu_price')}</label>
                  <input
                    type="text"
                    value={formData.price}
                    onChange={e => setFormData({ ...formData, price: e.target.value })}
                    className="w-full bg-black border border-white/10 rounded-2xl py-4 px-6 text-white focus:border-[#F57F25] outline-none"
                    placeholder={t('admin_menu_price_placeholder')}
                    required
                  />
                </div>
              </div>

              {/* Bilingual Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <label className="text-white/50 text-[10px] font-black uppercase tracking-widest ml-1">Name (English)</label>
                    <button
                      type="button"
                      onClick={() => handleTranslate('name', 'es', 'en')}
                      className="text-[#F57F25] text-[10px] font-black uppercase tracking-widest hover:text-[#e06d15] transition-colors"
                    >
                      Translate from Spanish
                    </button>
                  </div>
                  <input
                    type="text"
                    value={formData.nameEn}
                    onChange={e => setFormData({ ...formData, nameEn: e.target.value })}
                    className="w-full bg-black border border-white/10 rounded-2xl py-4 px-6 text-white focus:border-[#F57F25] outline-none"
                    placeholder="e.g. Burgers"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <label className="text-white/50 text-[10px] font-black uppercase tracking-widest ml-1">Nombre (Español)</label>
                    <button
                      type="button"
                      onClick={() => handleTranslate('name', 'en', 'es')}
                      className="text-[#F57F25] text-[10px] font-black uppercase tracking-widest hover:text-[#e06d15] transition-colors"
                    >
                      Traducir de Inglés
                    </button>
                  </div>
                  <input
                    type="text"
                    value={formData.nameEs}
                    onChange={e => setFormData({ ...formData, nameEs: e.target.value })}
                    className="w-full bg-black border border-white/10 rounded-2xl py-4 px-6 text-white focus:border-[#F57F25] outline-none"
                    placeholder="ej. Hamburguesas"
                    required
                  />
                </div>
              </div>

              {/* Category and Link */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <label className="text-white/50 text-[10px] font-black uppercase tracking-widest ml-1">{t('admin_menu_category')}</label>
                  <select
                    value={formData.categoryId}
                    onChange={e => setFormData({ ...formData, categoryId: e.target.value })}
                    className="w-full bg-black border border-white/10 rounded-2xl py-4 px-6 text-white focus:border-[#F57F25] outline-none appearance-none cursor-pointer"
                    required
                  >
                    <option value="" disabled>{t('admin_menu_select_cat')}</option>
                    {categories.map(cat => {
                      const catName = typeof cat.name === 'string' ? cat.name : (cat.name[i18n.language] || cat.name.en || '');
                      return (
                        <option key={cat.id} value={cat.id}>
                          {catName}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-white/50 text-[10px] font-black uppercase tracking-widest ml-1">{t('admin_menu_link')}</label>
                  <div className="relative">
                    <LinkIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20" size={18} />
                    <input
                      type="url"
                      value={formData.buyUrl}
                      onChange={e => setFormData({ ...formData, buyUrl: e.target.value })}
                      className="w-full bg-black border border-white/10 rounded-2xl py-4 pl-12 pr-6 text-white focus:border-[#F57F25] outline-none transition-all"
                      placeholder={t('admin_menu_link_placeholder')}
                    />
                  </div>
                </div>
              </div>

              {/* Bilingual Description */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <label className="text-white/50 text-[10px] font-black uppercase tracking-widest ml-1">Description (English)</label>
                    <button
                      type="button"
                      onClick={() => handleTranslate('description', 'es', 'en')}
                      className="text-[#F57F25] text-[10px] font-black uppercase tracking-widest hover:text-[#e06d15] transition-colors"
                    >
                      Translate from Spanish
                    </button>
                  </div>
                  <textarea
                    value={formData.descriptionEn}
                    onChange={e => setFormData({ ...formData, descriptionEn: e.target.value })}
                    className="w-full bg-black border border-white/10 rounded-2xl py-4 px-6 text-white focus:border-[#F57F25] outline-none h-28 resize-none"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <label className="text-white/50 text-[10px] font-black uppercase tracking-widest ml-1">Descripción (Español)</label>
                    <button
                      type="button"
                      onClick={() => handleTranslate('description', 'en', 'es')}
                      className="text-[#F57F25] text-[10px] font-black uppercase tracking-widest hover:text-[#e06d15] transition-colors"
                    >
                      Traducir de Inglés
                    </button>
                  </div>
                  <textarea
                    value={formData.descriptionEs}
                    onChange={e => setFormData({ ...formData, descriptionEs: e.target.value })}
                    className="w-full bg-black border border-white/10 rounded-2xl py-4 px-6 text-white focus:border-[#F57F25] outline-none h-28 resize-none"
                    required
                  />
                </div>
              </div>

              <div className="space-y-4">
                <label className="text-white/50 text-[10px] font-black uppercase tracking-widest ml-1">{t('admin_menu_image')}</label>
                <div className="flex items-center gap-6">
                  <div className="w-32 h-32 bg-black border border-white/10 rounded-[32px] flex items-center justify-center overflow-hidden flex-shrink-0">
                    {formData.image ? (
                      <img src={formData.image} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon className="text-white/10" size={40} />
                    )}
                  </div>
                  <div className="flex-1 space-y-3">
                    <input
                      type="file"
                      id="image-upload"
                      onChange={handleImageUpload}
                      className="hidden"
                      accept="image/*"
                    />
                    <label
                      htmlFor="image-upload"
                      className="inline-flex items-center gap-3 bg-white text-black font-black px-6 py-3 rounded-2xl cursor-pointer transition-all hover:bg-[#F57F25] text-xs uppercase"
                    >
                      {uploading ? <Loader2 className="animate-spin" size={18} /> : <ImageIcon size={18} />}
                      {uploading ? t('admin_menu_uploading') : t('admin_menu_upload')}
                    </label>
                  </div>
                </div>
              </div>

              <div className="pt-6">
                <button
                  type="submit"
                  disabled={loading || uploading || !formData.categoryId}
                  className="w-full bg-[#F57F25] hover:bg-[#e06d15] text-black font-black py-6 rounded-[24px] transition-all shadow-xl shadow-[#F57F25]/20 flex items-center justify-center gap-3 disabled:opacity-50 uppercase text-sm"
                >
                  <Save size={24} />
                  {editingItem ? t('admin_menu_update') : t('admin_menu_publish')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
